import { PolicyService } from '@benefitsui/app/services/policy/policy.service';
import { Component, ChangeDetectionStrategy, inject, viewChild, computed, resource, effect } from '@angular/core';
import { PolicyClient, FlagsApiResponseOfPolicyOverviewResponse } from '@benefitsui/app/api/benefits-clients';
import { firstValueFrom } from 'rxjs';
import {
  ButtonInput,
  StatusIndicatorInput
} from '@benefitsui/app/reusable-components/list-card/list-card.components.types';
import { TabsComponent } from '@dxo/ng-swift-ui';
import { environment } from '@benefitsui/environments/environment';
import { BreadcrumbService } from '@benefitsui/app/reusable-components/breadcrumbs/breadcrumb.service';

interface ClaimReport {
  title: string;
  bodyText: string;
  buttons: ButtonInput[];
  statuses: StatusIndicatorInput[];
}
import {
  DISABILITY_PLAN_CODES,
  DISABILITY_PAYMENT_PLAN_CODES,
  ASO_STD_FULL_SERVICE_PLAN_CODES,
  LIFE_PLAN_CODES
} from '../list/data/plan-code-mapping';

@Component({
  selector: 'app-claims-report',
  templateUrl: './claims-report.component.html',
  styleUrls: ['./claims-report.component.scss'],
  standalone: false,
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class ClaimsReportPageComponent {
  // ===== DEPENDENCY INJECTION =====
  private policyService = inject(PolicyService);
  private policyClient = inject(PolicyClient);
  private breadcrumbService = inject(BreadcrumbService);

  // ===== VIEW CHILD REFERENCES =====
  readonly tabsComponent = viewChild<TabsComponent>('tabsRef');

  // ===== CONSTANTS =====
  readonly ViewReportLabel = 'View report';
  readonly ActionLabel = 'Action';
  readonly ActionKey = 'action';
  readonly AlertText = 'Reports will be available once your coverages become effective. Check back soon for updates!';
  readonly LifeTabLabel = 'Life';
  readonly DisabilityTabLabel = 'Disability';

  // ===== PUBLIC PROPERTIES =====
  groupId = '';
  holderName = '';

  // ===== RESOURCES =====
  private policyOverviewResource = resource({
    params: () => ({}), // No params needed for overview call
    loader: () => firstValueFrom(this.policyClient.overview())
  });

  // ===== COMPUTED SIGNALS =====
  // Single signal for all coverage codes
  coverageCodes = computed(() => {
    if (!this.policyOverviewResource.hasValue()) return [];
    const response = this.policyOverviewResource.value() as FlagsApiResponseOfPolicyOverviewResponse;
    return response?.data?.policyCoverages?.map((coverage) => coverage.coverageCode) || [];
  });

  // Computed signals based on coverage codes signal
  hasLifeClaims = computed(() => {
    const codes = this.coverageCodes();
    return codes.some((code) => LIFE_PLAN_CODES.has(code));
  });

  hasDisabilityClaims = computed(() => {
    const codes = this.coverageCodes();
    return codes.some((code) => DISABILITY_PLAN_CODES.has(code));
  });

  hasDisabilityPaymentReports = computed(() => {
    const codes = this.coverageCodes();
    return codes.some((code) => DISABILITY_PAYMENT_PLAN_CODES.has(code));
  });

  hasIntegratedBenefitReport = computed(() => {
    const codes = this.coverageCodes();
    return codes.some((code) => ASO_STD_FULL_SERVICE_PLAN_CODES.has(code));
  });

  // Use resource loading state instead of manual signal
  isLoading = computed(() => this.policyOverviewResource.isLoading());

  // Computed signal for default tab selection
  selectedTab = computed(() => {
    if (!this.hasLifeClaims() && this.hasDisabilityClaims()) {
      return this.DisabilityTabLabel;
    }
    return this.LifeTabLabel;
  });

  // Disability reports - computed based on coverage signals
  disabilityReports = computed(() => {
    if (!this.hasDisabilityClaims()) {
      return [];
    }

    const reports: ClaimReport[] = [];

    // Always show Status report for disability
    reports.push({
      title: 'Status report',
      bodyText: 'STD, statutory disability, and LTD claim status',
      buttons: this.buttons,
      statuses: []
    });

    // Show Payment report if group has payment-eligible codes
    if (this.hasDisabilityPaymentReports()) {
      reports.push({
        title: 'Payment report',
        bodyText: 'STD, statutory disability, and LTD claim payments',
        buttons: this.buttons,
        statuses: []
      });
    }

    // Show Integrated benefit determination report for ASO STD Full Service
    if (this.hasIntegratedBenefitReport()) {
      reports.push({
        title: 'Integrated benefit determination report',
        bodyText: 'ASO STD with benefit calculations and company leave (if applicable) claim payment dates and amounts',
        buttons: this.buttons,
        statuses: []
      });
    }

    return reports;
  });

  // ===== STATIC DATA =====
  statusThing: StatusIndicatorInput[] = [
    {
      text: 'Status',
      color: 'green'
    }
  ];

  buttons: ButtonInput[] = [
    {
      label: this.ViewReportLabel,
      variant: 'secondary',
      icon: 'open_in_new'
      // No routerLink - don't link anywhere at this time
    }
  ];

  lifeReports: ClaimReport[] = [
    {
      title: 'Life status and payment report',
      bodyText: 'Life and AD&D claim and payment status',
      buttons: [
        {
          label: this.ViewReportLabel,
          variant: 'secondary',
          icon: 'open_in_new',
          href: `/benefits/claims/report-viewer?dashboardId=${environment.quicksight.lifeStatusDashboardId}`,
          newWindow: true,
          type: 'button'
        }
      ],
      statuses: []
    },
    {
      title: 'Waiver of premium claims status report',
      bodyText: 'Waiver of premium claim status',
      buttons: [
        {
          label: this.ViewReportLabel,
          variant: 'secondary',
          icon: 'open_in_new',
          href: `/benefits/claims/report-viewer?dashboardId=${environment.quicksight.wopStatusDashboardId}`,
          newWindow: true,
          type: 'button'
        }
      ],
      statuses: []
    }
  ];

  // ===== CONSTRUCTOR =====
  constructor() {
    this.groupId = this.policyService.groupId;
    this.holderName = this.policyService.policyName;

    // Effect to register tabs component when it becomes available
    effect(() => {
      const tabsComponent = this.tabsComponent();
      if (tabsComponent) {
        // For immediate behavior, we need to wait for data to load and then pass the selected tab
        // Use setTimeout to allow the component and data to stabilize
        setTimeout(() => {
          if (!this.isLoading()) {
            const selectedTab = this.selectedTab();
            console.log('ClaimsReportComponent: Registering tabs with selected tab:', selectedTab);
            this.breadcrumbService.registerTabsComponent(tabsComponent, selectedTab);
          } else {
            // If still loading, register without initial tab and let tab changes handle it
            console.log('ClaimsReportComponent: Still loading, registering without initial tab');
            this.breadcrumbService.registerTabsComponent(tabsComponent);
          }
        }, 100); // Small delay to allow data stabilization
      }
    });

    // Additional effect to handle immediate behavior after data loads
    effect(() => {
      // Watch for when loading completes
      const isLoading = this.isLoading();
      const tabsComponent = this.tabsComponent();
      
      if (!isLoading && tabsComponent) {
        // Data has loaded and tabs are available - show immediate tab if behavior is set to immediate
        setTimeout(() => {
          const selectedTab = this.selectedTab();
          console.log('ClaimsReportComponent: Data loaded, updating breadcrumb with selected tab:', selectedTab);
          // Re-register with the now-available selected tab for immediate behavior
          this.breadcrumbService.registerTabsComponent(tabsComponent, selectedTab);
        }, 50);
      }
    });
  }

  // ===== PUBLIC METHODS =====
  /**
   * Track by function for report lists
   */
  trackByReportId(report: ClaimReport): string {
    return report.title;
  }
}
